/**
 * Popup configurable (sections/popup-configurable.liquid).
 *
 * Portado de The Box. Abre según el disparador (retraso, scroll, intención de salida), recuerda
 * el cierre en cookie o en el almacenamiento del navegador y, en modo modal, atrapa el foco,
 * cierra con Escape o clic fuera y bloquea el scroll. En modo flotante (`data-modal="false"`)
 * no oscurece la página ni roba el foco: solo se muestra y se deja cerrar.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class PromoPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-promo-popup-dialog]');
    this.isDesignMode = this.dataset.designMode === 'true';
    this.isModal = this.dataset.modal !== 'false';
    this.lockScroll = this.isModal && this.dataset.lockScroll === 'true';
    this.duration = parseInt(getComputedStyle(this).getPropertyValue('--promo-pop-duration'), 10) || 300;
    this.isOpen = false;
    this.timers = [];

    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);

    this.querySelectorAll('[data-promo-popup-close]').forEach((el) => {
      el.addEventListener('click', (event) => {
        if (el.tagName === 'A') event.preventDefault();
        this.close();
      });
    });

    if (this.isModal && this.dataset.closeOnBackdrop === 'true') {
      const backdrop = this.querySelector('[data-promo-popup-backdrop]');
      if (backdrop) backdrop.addEventListener('click', () => this.close());
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });
    }

    // En el editor se muestra siempre para poder configurarlo.
    if (this.isDesignMode) {
      this.open();
      return;
    }

    if (this.alreadySeen()) return;
    this.arm();
  }

  disconnectedCallback() {
    this.teardown();
    this.unlock();
  }

  /* ---------- Disparadores ---------- */
  arm() {
    switch (this.dataset.trigger) {
      case 'scroll':
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
        break;
      case 'exit':
        document.addEventListener('mouseout', this.onMouseOut);
        // Respaldo en mobile, donde no existe la intención de salida
        this.timers.push(window.setTimeout(() => this.open(), 20000));
        break;
      default:
        this.timers.push(window.setTimeout(() => this.open(), parseInt(this.dataset.delay, 10) || 0));
    }
  }

  teardown() {
    this.timers.forEach((id) => window.clearTimeout(id));
    this.timers = [];
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseout', this.onMouseOut);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onScroll() {
    const target = parseInt(this.dataset.scrollPercent, 10) || 30;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const progress = max > 0 ? (window.scrollY / max) * 100 : 100;
    if (progress >= target) this.open();
  }

  onMouseOut(event) {
    if (event.clientY <= 0 && !event.relatedTarget) this.open();
  }

  /* ---------- Abrir / cerrar ---------- */
  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.teardown();
    this.lastFocused = document.activeElement;

    this.removeAttribute('hidden');
    // Reflow para que la transición de entrada se dispare
    this.getBoundingClientRect();
    this.classList.add('is-open');

    if (this.dataset.rememberOn === 'show') this.remember();

    if (this.lockScroll) document.documentElement.classList.add('promo-pop-locked');
    document.addEventListener('keydown', this.onKeydown);

    // Un aviso flotante no interrumpe lo que el visitante estaba haciendo.
    if (this.isModal) {
      const first = this.querySelector('[data-promo-popup-close]') || this.dialog;
      if (first) first.focus({ preventScroll: true });
    }

    this.dispatchEvent(new CustomEvent('promo-popup:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.onKeydown);
    this.unlock();
    if (this.dataset.rememberOn !== 'show') this.remember();

    window.setTimeout(() => {
      this.setAttribute('hidden', '');
    }, this.duration);

    if (this.isModal && this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus({ preventScroll: true });
    }

    this.dispatchEvent(new CustomEvent('promo-popup:close', { bubbles: true }));
  }

  unlock() {
    document.documentElement.classList.remove('promo-pop-locked');
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.dataset.closeOnEsc === 'true') {
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.isModal) return;

    const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- Recordar el cierre ---------- */
  /* `frequency` define durante cuánto tiempo se recuerda y `storage` dónde:
     en una cookie o en el almacenamiento del navegador. */
  alreadySeen() {
    const key = this.dataset.storageKey;
    const frequency = this.dataset.frequency;
    if (frequency === 'always') return false;

    if (this.dataset.storage === 'cookie') {
      // La caducidad la gestiona el propio navegador
      return document.cookie.split('; ').some((entry) => entry.split('=')[0] === key);
    }

    try {
      if (frequency === 'session') {
        return window.sessionStorage.getItem(key) === '1';
      }

      const stored = window.localStorage.getItem(key);
      if (!stored) return false;

      const until = parseInt(stored, 10);
      if (Number.isNaN(until)) return false;
      if (until === 0) return true; // 0 días = no volver a mostrar
      if (Date.now() < until) return true;

      window.localStorage.removeItem(key);
      return false;
    } catch (error) {
      return false;
    }
  }

  remember() {
    const key = this.dataset.storageKey;
    const frequency = this.dataset.frequency;
    if (frequency === 'always' || this.isDesignMode) return;

    const days = parseInt(this.dataset.frequencyDays, 10) || 0;

    if (this.dataset.storage === 'cookie') {
      let expires = '';
      if (frequency !== 'session') {
        // 0 días = no volver a mostrarlo nunca
        const until = days === 0 ? new Date(2999, 0, 1) : new Date(Date.now() + days * 86400000);
        expires = '; expires=' + until.toUTCString();
      }
      // Sin expires es una cookie de sesión: se borra al cerrar el navegador
      document.cookie = key + '=1' + expires + '; path=/; SameSite=Lax';
      return;
    }

    try {
      if (frequency === 'session') {
        window.sessionStorage.setItem(key, '1');
        return;
      }
      const value = days === 0 ? 0 : Date.now() + days * 86400000;
      window.localStorage.setItem(key, String(value));
    } catch (error) {
      /* almacenamiento no disponible */
    }
  }
}

if (!customElements.get('promo-popup')) {
customElements.define('promo-popup', PromoPopup);
}
